import { Link } from "react-router-dom";
import { ArrowLeft } from "lucide-react";

const SECTIONS = [
  {
    title: "1. Introduction",
    blocks: [
      { type: "p", text: "Welcome to NeXstrength Business (“NeXstrength,” “we,” “our,” or “us”)." },
      { type: "p", text: "These Terms and Conditions govern your access to and use of our website, business consultancy services, educational content, programs, workshops, consultations, and other services we may provide." },
      { type: "p", text: "By accessing our website, submitting an inquiry, booking a consultation, or purchasing a service, you agree to these Terms and Conditions. If you do not agree, please discontinue use of our services." },
    ],
  },
  {
    title: "2. About Our Services",
    blocks: [
      { type: "p", text: "NeXstrength provides business education, consultancy, and guidance designed to help business owners understand their business challenges and identify areas for improvement." },
      { type: "p", text: "Our approach may cover five key areas:" },
      {
        type: "ul",
        items: [
          "Mentality and decision-making.",
          "Finance and cash flow.",
          "Product management.",
          "Marketing.",
          "Sales.",
        ],
      },
      { type: "p", text: "The specific services, deliverables, duration, and fees will be communicated before a purchase or engagement, as applicable." },
    ],
  },
  {
    title: "3. No Guarantee of Business Results",
    blocks: [
      { type: "p", text: "Our services provide educational information, strategic guidance, and consultancy." },
      { type: "p", text: "Business outcomes depend on many factors, including your decisions, implementation, industry, market conditions, resources, and execution." },
      { type: "p", text: "We do not guarantee any specific revenue, profit, customer acquisition, business growth, or financial outcome. Examples, case studies, and testimonials, if provided, are illustrative and should not be interpreted as a promise of similar results." },
      { type: "p", text: "You remain responsible for decisions made and actions taken in your business." },
    ],
  },
  {
    title: "4. Eligibility and User Responsibilities",
    blocks: [
      { type: "p", text: "By using our services, you confirm that:" },
      {
        type: "ul",
        items: [
          "You are legally capable of entering into a binding agreement.",
          "The information you provide is accurate and not misleading.",
          "You will use our services for lawful purposes.",
          "You will communicate respectfully with our team and other participants.",
          "You will evaluate advice in light of your own circumstances before implementing it.",
          "You are responsible for obtaining any professional, financial, tax, legal, or regulatory advice your particular situation requires.",
        ],
      },
    ],
  },
  {
    title: "5. Consultations and Appointments",
    blocks: [
      { type: "p", text: "Consultations may require advance booking and confirmation." },
      { type: "p", text: "You agree to provide accurate contact information and attend appointments at the agreed time. If you need to reschedule or cancel, please notify us as early as possible." },
      { type: "p", text: "Any applicable cancellation, rescheduling, missed-appointment, or refund conditions will be communicated before booking or payment." },
      { type: "p", text: "We reserve the right to reschedule an appointment when reasonably necessary and will communicate any changes to the affected customer." },
    ],
  },
  {
    title: "6. Payments and Pricing",
    blocks: [
      { type: "p", text: "The applicable price, payment schedule, taxes, and included services will be displayed or communicated before you make a purchase." },
      { type: "p", text: "Unless otherwise stated, payment is required according to the terms presented at checkout or in the applicable service agreement." },
      { type: "p", text: "You agree to provide accurate payment information and pay all amounts lawfully due for the services you purchase." },
    ],
  },
  {
    title: "7. Refunds and Cancellations",
    blocks: [
      { type: "p", text: "Refund eligibility depends on the service purchased and the refund terms communicated at the time of purchase." },
      { type: "p", text: "Any applicable refund, cancellation, or withdrawal rights under law will be respected. Nothing in these Terms and Conditions is intended to exclude rights that cannot legally be waived." },
      { type: "p", text: "Please review the applicable offer terms before making a payment. For refund requests, contact us using the details provided below." },
    ],
  },
  {
    title: "8. Intellectual Property",
    blocks: [
      { type: "p", text: "Unless otherwise stated, the website, brand identity, logos, written materials, videos, course content, frameworks, graphics, and other materials provided by NeXstrength are owned by or licensed to us." },
      { type: "p", text: "You may use these materials for your personal learning or internal business purposes as permitted by your purchase or service agreement." },
      { type: "p", text: "You must not reproduce, resell, distribute, publish, modify, or commercially exploit our materials without prior written permission, except where permitted by applicable law." },
    ],
  },
  {
    title: "9. Acceptable Use",
    blocks: [
      { type: "p", text: "You agree not to:" },
      {
        type: "ul",
        items: [
          "Use our website or services for unlawful purposes.",
          "Attempt unauthorized access to our systems or accounts.",
          "Copy or distribute protected materials without authorization.",
          "Misrepresent your identity or provide deliberately false information.",
          "Interfere with the operation of our website or services.",
          "Harass, threaten, or abuse our team or other participants.",
        ],
      },
      { type: "p", text: "We may restrict access to our services where reasonably necessary to address misuse, security concerns, or breaches of these Terms, subject to applicable law and any contractual obligations." },
    ],
  },
  {
    title: "10. Third-Party Services",
    blocks: [
      { type: "p", text: "Our website or services may refer to or integrate with third-party platforms, including advertising platforms, scheduling tools, payment providers, communication services, and analytics tools." },
      { type: "p", text: "Those third parties may operate under their own terms and privacy policies. We are not responsible for independent third-party services beyond responsibilities imposed on us by applicable law or our own contractual commitments." },
    ],
  },
  {
    title: "11. Limitation of Liability",
    blocks: [
      { type: "p", text: "To the extent permitted by applicable law, NeXstrength will not be liable for indirect or consequential losses arising from the use of our website or services, including losses resulting from business decisions, lost opportunities, or anticipated profits." },
      { type: "p", text: "Nothing in these Terms excludes or limits liability where such exclusion or limitation is prohibited by law, including applicable consumer rights." },
      { type: "p", text: "These Terms do not remove your responsibility to make independent decisions about your business or to assess the suitability of any information provided." },
    ],
  },
  {
    title: "12. Service Availability and Changes",
    blocks: [
      { type: "p", text: "We aim to provide reliable access to our website and services but do not guarantee uninterrupted or error-free availability." },
      { type: "p", text: "We may update, improve, suspend, or discontinue parts of our website or services where reasonably necessary. Where a change materially affects a paid service, we will address it in accordance with the applicable agreement and law." },
    ],
  },
  {
    title: "13. Privacy",
    blocks: [
      { type: "p", text: "Our collection and use of personal information are described in our Privacy Policy." },
      { type: "p", text: "By using our services, you acknowledge that you can review that policy on our website. Any consent required for processing personal information will be handled separately where required by applicable law." },
    ],
  },
  {
    title: "14. Governing Law and Disputes",
    blocks: [
      { type: "p", text: "These Terms and Conditions are subject to applicable laws." },
      { type: "p", text: "Any dispute will be addressed through good-faith discussions where practical. If a dispute cannot be resolved informally, it will be handled by the courts or other competent dispute-resolution bodies with jurisdiction under applicable law." },
      { type: "p", text: "The final version of this section should identify the appropriate governing law and jurisdiction for the operating business." },
    ],
  },
  {
    title: "15. Changes to These Terms",
    blocks: [
      { type: "p", text: "We may revise these Terms and Conditions from time to time. The updated version will be published on our website with a revised date." },
      { type: "p", text: "Changes will apply as permitted by applicable law. Material changes to an existing paid engagement may also be subject to the applicable service agreement." },
    ],
  },
  {
    title: "16. Contact Information",
    blocks: [
      { type: "p", text: "For questions about these Terms and Conditions, our services, or a purchase, please contact us:" },
      { type: "p", text: "Email: nexstrengthbusiness@gmail.com" },
    ],
  },
];

export default function TermsAndConditions() {
  return (
    <div className="min-h-screen bg-white text-[#0A0A0A]" data-testid="terms-page">
      <main className="mx-auto w-full max-w-[680px] px-5 pb-20 pt-10 sm:pt-14">
        <Link
          to="/"
          data-testid="terms-back-link"
          className="inline-flex items-center gap-2 text-[13px] font-bold text-neutral-400 transition-colors hover:text-[#F25C05]"
        >
          <ArrowLeft className="h-4 w-4" /> Back to home
        </Link>
        <h1 className="mt-6 text-[clamp(1.8rem,6vw,2.5rem)] font-extrabold leading-[1.15] tracking-[-0.02em]">
          Terms and Conditions — NeXstrength Business
        </h1>
        <p className="mt-2.5 text-[13px] font-semibold text-neutral-400">Last Updated: 20 October 2026</p>

        <div className="mt-8 flex flex-col gap-6">
          {SECTIONS.map((s) => (
            <section key={s.title}>
              <h2 className="text-[16.5px] font-extrabold tracking-tight">{s.title}</h2>
              {s.blocks.map((b, i) =>
                b.type === "ul" ? (
                  <ul key={i} className="mt-2 flex flex-col gap-1.5">
                    {b.items.map((it) => (
                      <li key={it} className="flex gap-2.5 text-[14px] font-medium leading-[1.65] text-[#333333]">
                        <span className="mt-[9px] h-1.5 w-1.5 shrink-0 rounded-full bg-[#F25C05]" />
                        {it}
                      </li>
                    ))}
                  </ul>
                ) : (
                  <p key={i} className="mt-2 text-[14px] font-medium leading-[1.7] text-[#333333]">
                    {b.text}
                  </p>
                )
              )}
            </section>
          ))}
        </div>

        <div className="mt-12 rounded-[18px] border border-neutral-100 p-5 text-center shadow-[0_10px_30px_-18px_rgba(0,0,0,0.2)]">
          <p className="text-[13px] font-bold">Questions about these Terms?</p>
          <a href="mailto:nexstrengthbusiness@gmail.com" className="mt-1 inline-block text-[13.5px] font-bold text-[#F25C05]">
            nexstrengthbusiness@gmail.com
          </a>
        </div>
      </main>
    </div>
  );
}
